import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { purchaseCenterHooks } from '../../hooks/useMasters.js';
import Card from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Modal from '../../components/ui/Modal.jsx';

export default function PurchaseCentersPage() {
  const { data: centers = [], isLoading } = purchaseCenterHooks.useList();
  const create = purchaseCenterHooks.useCreate();
  const update = purchaseCenterHooks.useUpdate();
  const del = purchaseCenterHooks.useDelete();

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm();

  const openCreate = () => { setEditing(null); reset({}); setModalOpen(true); };
  const openEdit = (c) => { setEditing(c); reset(c); setModalOpen(true); };

  const onSubmit = async (values) => {
    editing ? await update.mutateAsync({ id: editing.id, ...values }) : await create.mutateAsync(values);
    setModalOpen(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Purchase Centers</h2>
          <p className="text-sm text-gray-500">Procurement centers for raw material purchasing</p>
        </div>
        <Button icon={Plus} onClick={openCreate}>Add Purchase Center</Button>
      </div>
      <Card>
        {isLoading ? <div className="p-8 text-center text-gray-400">Loading…</div> : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>{['Center Name', 'Address', ''].map(h => (
                <th key={h} className="text-left px-4 py-3 font-medium text-gray-600 text-xs uppercase">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {centers.map(c => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">{c.name}</td>
                  <td className="px-4 py-3 text-gray-600">{c.address || '—'}</td>
                  <td className="px-4 py-3 text-right flex gap-2 justify-end">
                    <button onClick={() => openEdit(c)} className="text-gray-400 hover:text-teal-600"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => del.mutate(c.id)} className="text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit Purchase Center' : 'Add Purchase Center'}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input label="Center Name" required {...register('name', { required: true })} error={errors.name && 'Required'} />
          <Input label="Address" {...register('address')} />
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" type="button" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button type="submit" loading={isSubmitting}>Save</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
